/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row, UncontrolledTooltip } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getAccountingTransactions } from 'app/entities/accounting-transaction/accounting-transaction.reducer';
import { getEntities as getAccountingBankAccounts } from 'app/entities/accounting-bank-account/accounting-bank-account.reducer';
import { getEntities as getClinicalDictionaries } from 'app/entities/clinical-dictionary/clinical-dictionary.reducer';
import { AddedByType } from 'app/shared/model/enumerations/added-by-type.model';
import { createEntity, getEntity, reset, updateEntity } from './accounting-collection-ledger.reducer';

export const AccountingCollectionLedgerUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const accountingTransactions = useAppSelector(state => state.accountingTransaction.entities);
  const accountingBankAccounts = useAppSelector(state => state.accountingBankAccount.entities);
  const clinicalDictionaries = useAppSelector(state => state.clinicalDictionary.entities);
  const accountingCollectionLedgerEntity = useAppSelector(state => state.accountingCollectionLedger.entity);
  const loading = useAppSelector(state => state.accountingCollectionLedger.loading);
  const updating = useAppSelector(state => state.accountingCollectionLedger.updating);
  const updateSuccess = useAppSelector(state => state.accountingCollectionLedger.updateSuccess);
  const addedByTypeValues = Object.keys(AddedByType);

  const handleClose = () => {
    navigate(`/accounting-collection-ledger${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getAccountingTransactions({}));
    dispatch(getAccountingBankAccounts({}));
    dispatch(getClinicalDictionaries({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.amount !== undefined && typeof values.amount !== 'number') {
      values.amount = Number(values.amount);
    }
    if (values.remarks !== undefined && typeof values.remarks !== 'number') {
      values.remarks = Number(values.remarks);
    }
    if (values.pendingAmount !== undefined && typeof values.pendingAmount !== 'number') {
      values.pendingAmount = Number(values.pendingAmount);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...accountingCollectionLedgerEntity,
      ...values,
      expense: accountingTransactions.find(it => it.id.toString() === values.expense?.toString()),
      income: accountingTransactions.find(it => it.id.toString() === values.income?.toString()),
      account: accountingBankAccounts.find(it => it.id.toString() === values.account?.toString()),
      entityType: clinicalDictionaries.find(it => it.id.toString() === values.entityType?.toString()),
      accountingTransactions: mapIdList(values.accountingTransactions),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          collectedByType: 'ACCOUNTANT',
          depositedByType: 'ACCOUNTANT',
          ...accountingCollectionLedgerEntity,
          expense: accountingCollectionLedgerEntity?.expense?.id,
          income: accountingCollectionLedgerEntity?.income?.id,
          account: accountingCollectionLedgerEntity?.account?.id,
          entityType: accountingCollectionLedgerEntity?.entityType?.id,
          accountingTransactions: accountingCollectionLedgerEntity?.accountingTransactions?.map(e => e.id.toString()),
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2
            id="tinyMedicRestApp.accountingCollectionLedger.home.createOrEditLabel"
            data-cy="AccountingCollectionLedgerCreateUpdateHeading"
          >
            <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.home.createOrEditLabel">
              Create or edit a AccountingCollectionLedger
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="accounting-collection-ledger-id"
                  label={translate('tinyMedicRestApp.accountingCollectionLedger.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.ledgerNo')}
                id="accounting-collection-ledger-ledgerNo"
                name="ledgerNo"
                data-cy="ledgerNo"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <UncontrolledTooltip target="ledgerNoLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.ledgerNo" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.amount')}
                id="accounting-collection-ledger-amount"
                name="amount"
                data-cy="amount"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="amountLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.amount" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.remarks')}
                id="accounting-collection-ledger-remarks"
                name="remarks"
                data-cy="remarks"
                type="text"
              />
              <UncontrolledTooltip target="remarksLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.remarks" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.pendingAmount')}
                id="accounting-collection-ledger-pendingAmount"
                name="pendingAmount"
                data-cy="pendingAmount"
                type="text"
              />
              <UncontrolledTooltip target="pendingAmountLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.pendingAmount" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.collectedById')}
                id="accounting-collection-ledger-collectedById"
                name="collectedById"
                data-cy="collectedById"
                type="text"
              />
              <UncontrolledTooltip target="collectedByIdLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.collectedById" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.collectedByType')}
                id="accounting-collection-ledger-collectedByType"
                name="collectedByType"
                data-cy="collectedByType"
                type="select"
              >
                {addedByTypeValues.map(addedByType => (
                  <option value={addedByType} key={addedByType}>
                    {translate(`tinyMedicRestApp.AddedByType.${addedByType}`)}
                  </option>
                ))}
              </ValidatedField>
              <UncontrolledTooltip target="collectedByTypeLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.collectedByType" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.depositedById')}
                id="accounting-collection-ledger-depositedById"
                name="depositedById"
                data-cy="depositedById"
                type="text"
              />
              <UncontrolledTooltip target="depositedByIdLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.depositedById" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.depositedByType')}
                id="accounting-collection-ledger-depositedByType"
                name="depositedByType"
                data-cy="depositedByType"
                type="select"
              >
                {addedByTypeValues.map(addedByType => (
                  <option value={addedByType} key={addedByType}>
                    {translate(`tinyMedicRestApp.AddedByType.${addedByType}`)}
                  </option>
                ))}
              </ValidatedField>
              <UncontrolledTooltip target="depositedByTypeLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.depositedByType" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.closed')}
                id="accounting-collection-ledger-closed"
                name="closed"
                data-cy="closed"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="closedLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.closed" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.entityId')}
                id="accounting-collection-ledger-entityId"
                name="entityId"
                data-cy="entityId"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.active')}
                id="accounting-collection-ledger-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="activeLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.active" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.cursor')}
                id="accounting-collection-ledger-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="cursorLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.cursor" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.lastUpdated')}
                id="accounting-collection-ledger-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="lastUpdatedLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.lastUpdated" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.microSec')}
                id="accounting-collection-ledger-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="microSecLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.microSec" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.timestampEntry')}
                id="accounting-collection-ledger-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="timestampEntryLabel">
                <Translate contentKey="tinyMedicRestApp.accountingCollectionLedger.help.timestampEntry" />
              </UncontrolledTooltip>
              <ValidatedField
                id="accounting-collection-ledger-expense"
                name="expense"
                data-cy="expense"
                label={translate('tinyMedicRestApp.accountingCollectionLedger.expense')}
                type="select"
              >
                <option value="" key="0" />
                {accountingTransactions
                  ? accountingTransactions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="accounting-collection-ledger-income"
                name="income"
                data-cy="income"
                label={translate('tinyMedicRestApp.accountingCollectionLedger.income')}
                type="select"
              >
                <option value="" key="0" />
                {accountingTransactions
                  ? accountingTransactions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="accounting-collection-ledger-account"
                name="account"
                data-cy="account"
                label={translate('tinyMedicRestApp.accountingCollectionLedger.account')}
                type="select"
              >
                <option value="" key="0" />
                {accountingBankAccounts
                  ? accountingBankAccounts.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="accounting-collection-ledger-entityType"
                name="entityType"
                data-cy="entityType"
                label={translate('tinyMedicRestApp.accountingCollectionLedger.entityType')}
                type="select"
              >
                <option value="" key="0" />
                {clinicalDictionaries
                  ? clinicalDictionaries.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.accountingCollectionLedger.accountingTransaction')}
                id="accounting-collection-ledger-accountingTransaction"
                data-cy="accountingTransaction"
                type="select"
                multiple
                name="accountingTransactions"
              >
                <option value="" key="0" />
                {accountingTransactions
                  ? accountingTransactions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button
                tag={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/accounting-collection-ledger"
                replace
                color="info"
              >
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AccountingCollectionLedgerUpdate;
